import { type Page, request } from './client'

/** Typed access to the customer-scoped portal endpoints. */

export interface Application {
  id: string
  name: string
  description: string
  status: string
  allowed_ip_cidrs: string[]
  active_credential_count: number
  /** Operations this application may call. Access is per application, so two
   *  applications of one customer can legitimately differ. */
  granted_operation_count: number
  /** Requests in the last 30 days — enough to tell a live integration from a
   *  forgotten one. Absent on a server older than this field. */
  recent_request_count?: number
  last_request_at?: string | null
  created_at: string
  updated_at: string
}

export interface Credential {
  id: string
  client_id: string
  label: string
  status: 'active' | 'revoked'
  created_at: string
  expires_at: string | null
  last_used_at: string | null
  revoked_at: string | null
}

export interface IssuedCredential {
  credential: Credential
  client_secret: string
  warning: string
}

/** A credential seen from the account rather than from one application. */
export interface AccountCredential extends Credential {
  application: string
  application_name: string
}

export interface Member {
  id: string
  /** Masked, as the server sends it. A member list is a colleague directory,
   *  and the reader already knows whose numbers these are. */
  mobile: string
  full_name: string
  role: string
  is_active: boolean
  /** Whether this person has ever signed in. Replaced `has_activated`, which
   *  asked whether they had set a password — a question with no meaning since
   *  sign-in became a code sent to a mobile. */
  has_signed_in: boolean
  created_at: string
}

export interface Recharge {
  id: string
  amount_irr: number
  status: 'pending' | 'settled' | 'failed'
  /** Ours, generated. What a customer quotes at us about this request. */
  reference: string
  created_at: string
  settled_at: string | null
}

export interface WalletSummary {
  posted_balance_irr: number
  held_balance_irr: number
  available_balance_irr: number
  currency: string
}

export interface LedgerEntry {
  id: string
  entry_type: string
  amount_irr: number
  balance_after_irr: number
  description: string
  /** The tracking id of the request this movement belongs to, blank for a
   *  recharge or an operator adjustment. The server has always sent it; the
   *  ledger simply could not say which call a debit came from without it. */
  request_id: string
  api_request: string | null
  /** Set on a reversing entry, pointing at the entry it reverses. The original
   *  is never rewritten, so a refund is two rows rather than an edit. */
  reverses: string | null
  created_at: string
}

export interface ApiPermission {
  id: string
  /** The application this grant belongs to. The server has always sent it;
   *  it was simply not declared here, so no screen could filter by it. */
  application: string
  application_name: string
  operation_code: string
  operation_name: string
  /** Persian display name for the customer UI; server falls back to the
   *  English name only if unset. */
  display_name_fa: string
  // Null while the operation has no price set — the serialiser declares
  // allow_null=True. Promising a number here is what let the admin console
  // crash on a fresh platform.
  sale_price_irr: number | null
  is_enabled: boolean
}

export interface ApiRequestRow {
  id: string
  request_id: string
  /** The caller's own identifier, when they supplied one. */
  client_request_id: string
  application: string
  application_name: string
  operation_code: string
  /** The customer-facing Persian name. Optional so a portal build pointed at
   *  an older server falls back to the code rather than rendering nothing. */
  operation_display_name_fa?: string
  status: string
  http_status: number | null
  error_code: string
  sale_price_irr: number
  charged_amount_irr: number
  refunded_amount_irr: number
  /** The four fields that are authoritative about what a call cost.
   *
   *  Not inferable from the status: a released hold answers `succeeded` having
   *  charged nothing, and a business rejection answers `failed` having charged
   *  in full. Optional so an older server that omits them reads as absent
   *  rather than as zero. */
  quoted_price_irr?: number
  charged_irr?: number
  refunded_irr?: number
  net_wallet_delta_irr?: number
  duration_ms: number | null
  created_at: string
  completed_at: string | null
}

const scope = (customerId: string) => `/customers/${customerId}`

export const listApplications = (customerId: string, signal?: AbortSignal) =>
  request<Page<Application>>(`${scope(customerId)}/applications/`, signal ? { signal } : {})

export const getApplication = (customerId: string, id: string, signal?: AbortSignal) =>
  request<Application>(`${scope(customerId)}/applications/${id}/`, signal ? { signal } : {})

/* Applications, and the client id/secret pairs inside them, are provisioned by
   an operator. The portal reads them and does not create, suspend, rotate or
   revoke any of it: a customer administering their own access is the thing the
   read-mostly model exists to prevent, and every one of those actions has an
   operator behind it who can be asked.

   The one exception is the API access token below. That is the credential the
   customer's own product is configured with, and making somebody file a ticket
   to obtain it would be making them wait to use what they are paying for. */
export const listCredentials = (customerId: string, applicationId: string, signal?: AbortSignal) =>
  request<Credential[]>(
    `${scope(customerId)}/applications/${applicationId}/credentials/`,
    signal ? { signal } : {},
  )

export const listAccountCredentials = (customerId: string, signal?: AbortSignal) =>
  request<AccountCredential[]>(`${scope(customerId)}/credentials/`, signal ? { signal } : {})

export const listMembers = (customerId: string, signal?: AbortSignal) =>
  request<Member[]>(`${scope(customerId)}/members/`, signal ? { signal } : {})

export const getWallet = (customerId: string, signal?: AbortSignal) =>
  request<WalletSummary>(`${scope(customerId)}/wallet/`, signal ? { signal } : {})

export const getLedger = (customerId: string, page: number, signal?: AbortSignal) =>
  request<Page<LedgerEntry>>(`${scope(customerId)}/wallet/ledger/`, {
    query: { page },
    ...(signal ? { signal } : {}),
  })

export const listPermissions = (customerId: string, signal?: AbortSignal) =>
  request<Page<ApiPermission>>(`${scope(customerId)}/api-permissions/`, signal ? { signal } : {})

export const listRequests = (
  customerId: string,
  page: number,
  filters: { status?: string; application?: string } = {},
  signal?: AbortSignal,
) =>
  request<Page<ApiRequestRow>>(`${scope(customerId)}/requests/`, {
    query: {
      page,
      ...(filters.status ? { status: filters.status } : {}),
      ...(filters.application ? { application: filters.application } : {}),
    },
    ...(signal ? { signal } : {}),
  })

/**
 * API access tokens — the credential a *consuming product* is configured with.
 *
 * Distinct from the client id and secret above, which our own SDK exchanges
 * for a short-lived token. This is one long-lived value a person pastes into
 * another product's settings screen, alongside the username. Both kinds may
 * exist on the same application.
 */
export interface ApiAccessToken {
  id: string
  name: string
  /** The leading characters, for telling tokens apart. Never the whole value. */
  token_prefix: string
  status: 'active' | 'revoked' | 'expired'
  application: string
  application_name: string
  customer_name: string
  /** What the consuming product must enter as its username. */
  username: string
  created_at: string
  expires_at: string | null
  revoked_at: string | null
  last_used_at: string | null
}

/** The only response that ever carries the token itself. */
export interface IssuedApiAccessToken {
  token: ApiAccessToken
  api_access_token: string
  username: string
}

export const listApiTokens = (customerId: string, applicationId: string, signal?: AbortSignal) =>
  request<ApiAccessToken[]>(
    `${scope(customerId)}/applications/${applicationId}/api-tokens/`,
    signal ? { signal } : {},
  )

export const createApiToken = (customerId: string, applicationId: string, name: string) =>
  request<IssuedApiAccessToken>(`${scope(customerId)}/applications/${applicationId}/api-tokens/`, {
    method: 'POST',
    body: { name },
  })

export const revokeApiToken = (customerId: string, applicationId: string, tokenId: string) =>
  request<ApiAccessToken>(
    `${scope(customerId)}/applications/${applicationId}/api-tokens/${tokenId}/revoke/`,
    { method: 'POST', body: {} },
  )

export const listRecharges = (customerId: string, signal?: AbortSignal) =>
  request<Recharge[]>(`${scope(customerId)}/wallet/recharges/`, signal ? { signal } : {})

/** Asks for credit. Creates a request and moves no money — the balance changes
 *  when somebody authorised settles it. */
export const requestRecharge = (customerId: string, amountIrr: number, note: string) =>
  request<Recharge>(`${scope(customerId)}/wallet/recharges/`, {
    method: 'POST',
    body: { amount_irr: amountIrr, note },
  })
